import { useState, useEffect } from 'react';
import { Building2, Save, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { profileApi } from '@/user/api';
import ProductImageUploader from '@/user/components/ProductImageUploader'; // 引入组件
import type { BuyerVO, SupplierVO } from '@/types/api-entity';
import { useLocation } from 'react-router-dom';
export default function ProfilePage() {
  const location = useLocation();
  const [buyer, setBuyer] = useState<BuyerVO | null>(null);
  const [supplier, setSupplier] = useState<SupplierVO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'buyer' | 'supplier'>(location.state ||'buyer');

  // Buyer form
  const [buyerForm, setBuyerForm] = useState<Partial<BuyerVO>>({});
  // Supplier form
  const [supplierForm, setSupplierForm] = useState<Partial<SupplierVO>>({});
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [buyerRes, supplierRes] = await Promise.all([
        profileApi.getMyBuyer(),
        profileApi.getMySupplier(),
      ]);
      if (buyerRes.code === 200 && buyerRes.data) {
        setBuyer(buyerRes.data);
        setBuyerForm(buyerRes.data);
      }
      if (supplierRes.code === 200 && supplierRes.data) {
        setSupplier(supplierRes.data);
        setSupplierForm(supplierRes.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBuyer = async () => {
    if (!buyerForm.companyName?.trim()) {
      setError('公司名称不能为空');
      return;
    }
    if (!buyerForm.contactName?.trim()) {
      setError('联系人不能为空');
      return;
    }
    if (!buyerForm.contactPhone?.trim()) {
      setError('联系电话不能为空');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await profileApi.saveMyBuyer(buyerForm);
      if (res.code === 200 && res.data) {
        setBuyer(res.data);
        setMessage('采购商资料保存成功');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSupplier = async () => {
    if (!supplierForm.companyName?.trim()) {
      setError('公司名称不能为空');
      return;
    }
    if (!supplierForm.contactName?.trim()) {
      setError('联系人不能为空');
      return;
    }
    if (!supplierForm.contactPhone?.trim()) {
      setError('联系电话不能为空');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await profileApi.saveMySupplier(supplierForm);
      if (res.code === 200 && res.data) {
        setSupplier(res.data);
        setMessage('供应商资料保存成功');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const getStatusText = (status?: number) => {
    switch (status) {
      case 0: return '待审核';
      case 1: return '已通过';
      case 2: return '已拒绝';
      default: return '未知';
    }
  };

  const getStatusColor = (status?: number) => {
    switch (status) {
      case 0: return 'text-amber-600 bg-amber-50';
      case 1: return 'text-green-600 bg-green-50';
      case 2: return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin" />
      </div>
    );
  }
  const handSubmit = (tab: 'buyer' | 'supplier') => { 
    setActiveTab(tab);
    setError('');
    setMessage('');
  };
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Building2 className="w-6 h-6 text-[#1c5bff]" />
        企业资料
      </h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { handSubmit('buyer')}}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'buyer'
              ? 'bg-[#1c5bff] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          采购商资料
          {buyer && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getStatusColor(buyer.status)}`}>
              {getStatusText(buyer.status)}
            </span>
          )}
        </button>
        <button
          onClick={() => { handSubmit('supplier') }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'supplier'
              ? 'bg-[#1c5bff] text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          供应商资料
          {supplier && (
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getStatusColor(supplier.status)}`}>
              {getStatusText(supplier.status)}
            </span>
          )}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        {activeTab === 'buyer' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">
              完善采购商资料后，即可在商城下单采购商品。提交后需等待平台审核。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={buyerForm.companyName || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, companyName: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司编码</label>
                <input
                  type="text"
                  value={buyerForm.companyCode || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, companyCode: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司编码"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系人 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={buyerForm.contactName || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, contactName: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系人姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系电话 <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={buyerForm.contactPhone || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, contactPhone: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系电话"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系邮箱</label>
                <input
                  type="email"
                  value={buyerForm.contactEmail || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, contactEmail: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系邮箱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司地址</label>
                <input
                  type="text"
                  value={buyerForm.address || ''}
                  onChange={(e) => setBuyerForm({ ...buyerForm, address: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司地址"
                />
              </div>
            </div>
            
            {/* 使用 ProductImageUploader 组件，设置 simple 模式 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">营业执照图片</label>
              <ProductImageUploader 
                value={buyerForm.businessLicense}
                onChange={(url) => setBuyerForm({ ...buyerForm, businessLicense: url })}
                simple
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">经营范围</label>
              <textarea
                value={buyerForm.businessScope || ''}
                onChange={(e) => setBuyerForm({ ...buyerForm, businessScope: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all resize-none"
                placeholder="请输入经营范围"
              />
            </div>
            <div className="pt-4">
              <button
                onClick={handleSaveBuyer}
                disabled={saving}
                className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存采购商资料
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">
              完善供应商资料后，即可发布商品。提交后需等待平台审核。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={supplierForm.companyName || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, companyName: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司编码</label>
                <input
                  type="text"
                  value={supplierForm.companyCode || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, companyCode: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司编码"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系人 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={supplierForm.contactName || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contactName: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系人姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系电话 <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={supplierForm.contactPhone || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contactPhone: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系电话"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系邮箱</label>
                <input
                  type="email"
                  value={supplierForm.contactEmail || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contactEmail: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入联系邮箱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司地址</label>
                <input
                  type="text"
                  value={supplierForm.address || ''}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all"
                  placeholder="请输入公司地址"
                />
              </div>
            </div>

            {/* 使用 ProductImageUploader 组件，设置 simple 模式 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">营业执照图片</label>
              <ProductImageUploader 
                value={supplierForm.businessLicense}
                onChange={(url) => setSupplierForm({ ...supplierForm, businessLicense: url })}
                simple
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">经营范围</label>
              <textarea
                value={supplierForm.businessScope || ''}
                onChange={(e) => setSupplierForm({ ...supplierForm, businessScope: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#1c5bff] focus:ring-2 focus:ring-[#1c5bff]/20 outline-none transition-all resize-none"
                placeholder="请输入经营范围"
              />
            </div>
            <div className="pt-4">
              <button
                onClick={handleSaveSupplier}
                disabled={saving}
                className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存供应商资料
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}